/**
 * BoSecretariatPlanningPage — Vue calendrier global du secrétariat.
 * Route : /bo/secretariat/planning
 *
 * Fonctionnalités :
 *  - Sélecteur de semaine (7 jours) avec navigation précédent/suivant/aujourd'hui
 *  - Grille horaire (7 colonnes × créneaux 30 min de 8h à 20h)
 *  - Chaque leçon = "card" positionnée par enseignant (couleur)
 *  - Drag & drop HTML5 natif : déplacer une leçon vers un autre créneau (change date + start_time)
 *  - Bouton "Ajouter une leçon" (formulaire minimal)
 *  - Filtre par enseignant (multi-select via chips)
 *
 * Contraintes techniques :
 *  - Pas de librairie de calendrier externe (drag-drop HTML5 natif)
 *  - Optimistic UI : la carte se déplace instantanément, rollback si erreur API
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Calendar, Plus, Filter,
  AlertCircle, X, GraduationCap, Users,
} from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';
const HOURS = Array.from({ length: 25 }, (_, i) => {
  const h = 8 + Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
}); // 08:00 → 20:00

// Utils dates
const toISODate = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = dt.getDay() || 7; // Lundi=1, Dimanche=7
  if (day !== 1) dt.setDate(dt.getDate() - (day - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
};

export default function BoSecretariatPlanningPage() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState(null); // null = tous
  const [dragging, setDragging] = useState(null); // {lesson_id, orig_date, orig_time}
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    student_file_id: '', teacher_file_id: '', date: toISODate(new Date()),
    start_time: '14:00', duration_min: 60, type: 'conduite', vehicle_type: 'bm', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const dateFrom = toISODate(days[0]);
  const dateTo = toISODate(days[6]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [l, t, s] = await Promise.all([
        api.boListLessons({ dateFrom, dateTo }),
        api.boListEnseignants(),
        api.boListEleves(),
      ]);
      setLessons(l.items || []);
      setTeachers(t.items || []);
      setStudents(s.items || []);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate('/login', { state: { from: '/bo/secretariat/planning' } });
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, navigate]);

  useEffect(() => {
    document.title = 'Planning global · Back-office';
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // === Filtrage par enseignant ===
  const filteredLessons = useMemo(() => {
    if (!selectedTeacherIds || selectedTeacherIds.length === 0) return lessons;
    return lessons.filter((l) => selectedTeacherIds.includes(l.teacher_file_id));
  }, [lessons, selectedTeacherIds]);

  // === Grouper par date-heure pour rendu ===
  // Retourne un Map: `${date}#${hh:mm}` → [lessons]
  const byCell = useMemo(() => {
    const m = new Map();
    filteredLessons.forEach((l) => {
      const key = `${l.date}#${(l.start_time || '00:00').slice(0, 5)}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(l);
    });
    return m;
  }, [filteredLessons]);

  // === Snap heure sur créneau 30 min le plus proche ===
  const snapTime = (hhmm) => {
    const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
    const total = h * 60 + m;
    const snapped = Math.round(total / 30) * 30;
    const nh = Math.floor(snapped / 60);
    const nm = snapped % 60;
    return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
  };

  // === Drag & Drop HTML5 ===
  const handleDragStart = (e, lesson) => {
    setDragging({ id: lesson.id, orig_date: lesson.date, orig_time: lesson.start_time });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lesson.id);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = async (e, targetDate, targetTime) => {
    e.preventDefault();
    if (!dragging) return;
    const snapped = snapTime(targetTime);
    // Si aucun changement → skip
    if (dragging.orig_date === targetDate && (dragging.orig_time || '').slice(0, 5) === snapped) {
      setDragging(null);
      return;
    }
    // Optimistic update
    setLessons((prev) =>
      prev.map((l) => (l.id === dragging.id ? { ...l, date: targetDate, start_time: snapped } : l))
    );
    try {
      await api.boUpdateLesson(dragging.id, { date: targetDate, start_time: snapped });
    } catch (err) {
      // Rollback
      setLessons((prev) =>
        prev.map((l) =>
          l.id === dragging.id ? { ...l, date: dragging.orig_date, start_time: dragging.orig_time } : l
        )
      );
      setError(`Impossible de déplacer : ${err.message}`);
    }
    setDragging(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.student_file_id || !addForm.teacher_file_id) {
      setError('Sélectionne un élève ET un enseignant');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.boCreateLesson(addForm);
      setShowAdd(false);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (l) => {
    if (!window.confirm(`Supprimer la leçon du ${l.date} à ${l.start_time} ?`)) return;
    try {
      await api.boDeleteLesson(l.id);
      reload();
    } catch (err) { setError(err.message); }
  };

  const toggleTeacher = (id) => {
    setSelectedTeacherIds((prev) => {
      if (!prev) return teachers.filter((t) => t.id !== id).map((t) => t.id);
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        return next.length === 0 ? null : next;
      }
      const next = [...prev, id];
      return next.length === teachers.length ? null : next;
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-8" data-testid="bo-planning-global">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-3 ml-2 sm:ml-0">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 px-2 sm:px-0">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BO_BLUE }}>
            Secrétariat
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Planning global
          </h1>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-black shadow-sm hover:opacity-90"
          style={{ background: BO_BLUE }}
          data-testid="btn-add-lesson-global"
        >
          <Plus className="w-4 h-4" /> Nouvelle leçon
        </button>
      </div>

      {/* Nav semaine */}
      <div className="flex items-center gap-2 mb-3 px-2 sm:px-0">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="p-2 rounded-lg border-2 border-border hover:bg-muted"
          data-testid="btn-week-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => setWeekStart(startOfWeek(new Date()))}
          className="px-3 py-2 rounded-lg border-2 border-border text-xs font-black hover:bg-muted"
          data-testid="btn-week-today"
        >
          Aujourd&apos;hui
        </button>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="p-2 rounded-lg border-2 border-border hover:bg-muted"
          data-testid="btn-week-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-xs sm:text-sm text-foreground/70 font-bold ml-1 flex-1">
          <Calendar className="w-3.5 h-3.5 inline mr-1" />
          {days[0].toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} —{' '}
          {days[6].toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      </div>

      {/* Filtre enseignants (chips) */}
      {teachers.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3 px-2 sm:px-0" data-testid="teacher-filter-chips">
          <Filter className="w-3.5 h-3.5 text-foreground/50" />
          {teachers.map((t) => {
            const on = !selectedTeacherIds || selectedTeacherIds.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggleTeacher(t.id)}
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded transition ${on ? 'text-white' : 'bg-muted text-foreground/50 hover:bg-muted/80'}`}
                style={on ? { background: t.color || BO_BLUE } : {}}
                data-testid={`filter-teacher-${t.id}`}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 mb-3 flex items-start gap-2 text-xs" data-testid="bo-planning-global-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Grille calendrier — DESKTOP (>= 768px) */}
      {loading ? (
        <p className="text-sm text-foreground/50 py-12 text-center">Chargement du planning…</p>
      ) : (
        <>
        <div className="hidden md:block rounded-xl border-2 border-border bg-card overflow-x-auto" data-testid="calendar-grid">
          <div className="min-w-[900px]">
            {/* Ligne header : jours */}
            <div className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b-2 border-border bg-muted/40 sticky top-0 z-10">
              <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 p-2">
                Heure
              </div>
              {days.map((d) => {
                const isToday = toISODate(d) === toISODate(new Date());
                return (
                  <div
                    key={d.toISOString()}
                    className={`p-2 border-l border-border text-center ${isToday ? 'bg-[#1d4ed8]/8' : ''}`}
                    data-testid={`day-header-${toISODate(d)}`}
                  >
                    <div className={`text-[9px] font-black uppercase tracking-widest ${isToday ? 'text-[#1d4ed8]' : 'text-foreground/50'}`}>
                      {d.toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </div>
                    <div className={`text-sm font-black ${isToday ? 'text-[#1d4ed8]' : 'text-foreground'}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Lignes horaires */}
            {HOURS.map((hhmm) => (
              <div
                key={hhmm}
                className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b border-border/50"
                style={{ minHeight: 36 }}
              >
                <div className="text-[10px] font-mono text-foreground/40 p-1 border-r border-border/50 text-right pr-2">
                  {hhmm.endsWith(':00') ? hhmm : ''}
                </div>
                {days.map((d) => {
                  const dateKey = toISODate(d);
                  const key = `${dateKey}#${hhmm}`;
                  const cellLessons = byCell.get(key) || [];
                  return (
                    <div
                      key={key}
                      className="border-l border-border/50 relative p-0.5"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, dateKey, hhmm)}
                      data-testid={`cell-${dateKey}-${hhmm}`}
                    >
                      {cellLessons.map((l) => {
                        const t = teachers.find((x) => x.id === l.teacher_file_id);
                        const color = t?.color || BO_BLUE;
                        const heightHalfSlots = Math.max(1, Math.round((l.duration_min || 60) / 30));
                        return (
                          <div
                            key={l.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, l)}
                            onDoubleClick={() => handleDeleteLesson(l)}
                            className="absolute inset-x-0.5 rounded px-1.5 py-1 text-white text-[10px] cursor-move hover:brightness-110 transition-all overflow-hidden"
                            style={{
                              background: color,
                              zIndex: 5,
                              height: heightHalfSlots * 36 - 4,
                              opacity: l.status === 'annule' ? 0.4 : 1,
                              textDecoration: l.status === 'annule' ? 'line-through' : 'none',
                            }}
                            title={`${l.student_name || '?'} — ${l.type} · ${l.duration_min}min\nDouble-click pour supprimer`}
                            data-testid={`lesson-card-${l.id}`}
                          >
                            <div className="font-black leading-tight truncate">{l.student_name || '?'}</div>
                            <div className="text-[9px] opacity-90 truncate">
                              {l.start_time} · {l.duration_min}min
                            </div>
                            <div className="text-[9px] opacity-75 truncate">{l.type}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Vue LISTE — MOBILE (< 768px) : groupée par jour */}
        <div className="md:hidden space-y-3" data-testid="calendar-mobile-list">
          {days.map((d) => {
            const dateKey = toISODate(d);
            const dayLessons = filteredLessons
              .filter((l) => l.date === dateKey)
              .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
            const isToday = dateKey === toISODate(new Date());
            return (
              <div
                key={dateKey}
                className={`rounded-xl border-2 ${isToday ? 'border-[#1d4ed8]' : 'border-border'} bg-card overflow-hidden`}
                data-testid={`mobile-day-${dateKey}`}
              >
                <div
                  className={`px-3 py-2 flex items-baseline justify-between ${isToday ? 'bg-[#1d4ed8]/8' : 'bg-muted/30'}`}
                >
                  <div>
                    <div
                      className={`text-[10px] font-black uppercase tracking-widest ${isToday ? 'text-[#1d4ed8]' : 'text-foreground/60'}`}
                    >
                      {d.toLocaleDateString('fr-FR', { weekday: 'long' })}
                    </div>
                    <div className={`text-lg font-black ${isToday ? 'text-[#1d4ed8]' : 'text-foreground'}`}>
                      {d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}
                    </div>
                  </div>
                  {isToday && (
                    <span className="text-[9px] font-black uppercase tracking-widest text-[#1d4ed8]">
                      Aujourd&apos;hui
                    </span>
                  )}
                  <span className="text-[10px] font-black text-foreground/50">
                    {dayLessons.length} leçon{dayLessons.length > 1 ? 's' : ''}
                  </span>
                </div>
                {dayLessons.length === 0 ? (
                  <div className="px-3 py-4 text-center text-[11px] text-foreground/40 italic">
                    Aucune leçon planifiée
                  </div>
                ) : (
                  <ul className="divide-y divide-border/50">
                    {dayLessons.map((l) => {
                      const t = teachers.find((x) => x.id === l.teacher_file_id);
                      const color = t?.color || BO_BLUE;
                      return (
                        <li
                          key={l.id}
                          className={`px-3 py-2.5 flex items-center gap-3 ${l.status === 'annule' ? 'opacity-40 line-through' : ''}`}
                          data-testid={`mobile-lesson-${l.id}`}
                        >
                          <div
                            className="flex-shrink-0 w-14 text-center py-1.5 rounded-lg"
                            style={{ background: `${color}20` }}
                          >
                            <div className="text-xs font-black" style={{ color }}>
                              {l.start_time}
                            </div>
                            <div className="text-[9px] font-black text-foreground/60">
                              {l.duration_min}min
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-black text-foreground truncate">
                              {l.student_name || '?'}
                            </div>
                            <div className="text-[10px] text-foreground/60 truncate">
                              {l.type} · avec{' '}
                              <span
                                className="inline-block px-1 rounded text-white text-[9px] font-black"
                                style={{ background: color }}
                              >
                                {l.teacher_name || t?.name || '?'}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteLesson(l)}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-600 flex-shrink-0"
                            title="Supprimer"
                            data-testid={`mobile-delete-${l.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      <p className="text-[10px] text-foreground/40 mt-3 text-center px-2 sm:px-0" data-testid="planning-help">
        💡 <span className="hidden md:inline"><strong>Glisser-déposer</strong> une carte pour la déplacer · <strong>Double-cliquer</strong> pour supprimer</span>
        <span className="md:hidden">Appuyez sur × pour supprimer une leçon · Passez sur ordinateur pour glisser-déposer</span>
      </p>

      {/* Modal ajout leçon */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form
            onSubmit={handleAdd}
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-2xl border-2 border-border max-w-md w-full p-5 sm:p-6 space-y-3 max-h-[90vh] overflow-y-auto"
            data-testid="modal-add-lesson-global"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Nouvelle leçon
              </h2>
              <button type="button" onClick={() => setShowAdd(false)}>
                <X className="w-5 h-5 text-foreground/60" />
              </button>
            </div>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60 flex items-center gap-1">
                <Users className="w-3 h-3" /> Élève *
              </span>
              <select
                required
                value={addForm.student_file_id}
                onChange={(e) => setAddForm({ ...addForm, student_file_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-global-student"
              >
                <option value="">— Choisir —</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60 flex items-center gap-1">
                <GraduationCap className="w-3 h-3" /> Enseignant *
              </span>
              <select
                required
                value={addForm.teacher_file_id}
                onChange={(e) => setAddForm({ ...addForm, teacher_file_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-global-teacher"
              >
                <option value="">— Choisir —</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Date *</span>
                <input
                  type="date" required
                  value={addForm.date}
                  onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                  className="w-full mt-1 px-2 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-global-date"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Heure *</span>
                <input
                  type="time" required
                  value={addForm.start_time}
                  onChange={(e) => setAddForm({ ...addForm, start_time: e.target.value })}
                  className="w-full mt-1 px-2 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-global-time"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Type</span>
                <select
                  value={addForm.type}
                  onChange={(e) => setAddForm({ ...addForm, type: e.target.value, duration_min: e.target.value === 'moto_plateau' ? 120 : 60 })}
                  className="w-full mt-1 px-2 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-global-type"
                >
                  <option value="conduite">Conduite</option>
                  <option value="code">Code</option>
                  <option value="examen">Examen</option>
                  <option value="rvp">RVP</option>
                  <option value="moto_plateau">Moto · Plateau</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Durée</span>
                <select
                  value={addForm.duration_min}
                  onChange={(e) => setAddForm({ ...addForm, duration_min: parseInt(e.target.value, 10) })}
                  className="w-full mt-1 px-2 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                >
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>120 min</option>
                </select>
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="flex-1 px-3 py-2 rounded-lg border-2 border-border text-sm font-black hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-[2] px-3 py-2 rounded-lg text-white text-sm font-black hover:opacity-90 disabled:opacity-40"
                style={{ background: BO_BLUE }}
                data-testid="btn-submit-lesson-global"
              >
                {saving ? 'Création…' : 'Créer la leçon'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
